import React from 'react';
import { Box, Button, ColumnLayout, Container, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { ColumnSourceType } from 'src/models/segment-cdt/SegmentCdtTypes';

export interface ColumnTypeOption {
    /** Stable key + React key. Defaults to sourceType when omitted. */
    key?: string;
    /** The source type this card picks. Omitted for a category card that opens a sub-chooser instead. */
    sourceType?: ColumnSourceType;
    title: string;
    description: string;
    icon?: string; // optional asset under IMAGE_PATH; omitted types render without one
    /** When set, Select calls this instead of onPick(sourceType) — e.g. a category card opening a sub-menu. */
    onSelect?: () => void;
}

// Named per-type options so callers compose by identity (reordering one can't mis-slot another).
export const GL_PULL_TYPE: ColumnTypeOption = {
    sourceType: ColumnSourceType.PULL_GL,
    icon: 'segment-gl-pull.svg',
    title: 'GL Balance Pull',
    description: 'Pull balances from the General Ledger for each value of the segment.',
};
export const MANUAL_TYPE: ColumnTypeOption = {
    sourceType: ColumnSourceType.MANUAL,
    icon: 'segment-manual-upload.svg',
    title: 'Manual Upload',
    description: 'Add a blank column and fill its values later with Upload.',
};
export const DERIVED_PCT_TYPE: ColumnTypeOption = {
    sourceType: ColumnSourceType.DERIVED_PCT,
    icon: 'segment-derived-pct.svg',
    title: '% of total',
    description: "Each row's share of a column's total (a Percentage column).",
};
export const DERIVED_BAL_TYPE: ColumnTypeOption = {
    sourceType: ColumnSourceType.DERIVED_BAL,
    icon: 'segment-derived-balance.svg',
    title: 'Derived balance',
    description: 'A % applied to an aggregated GL balance pull (a Number column).',
};

/** The two source types the create wizard offers. DERIVED_* are deliberately not among them. */
export const CREATE_COLUMN_TYPES: ColumnTypeOption[] = [GL_PULL_TYPE, MANUAL_TYPE];

/**
 * Column-type chooser. Renders a card per offered type. Defaults to the create wizard's two types.
 * An option with an `onSelect` runs that instead of picking a source type (used for the "Derived
 * Column" category card that opens the derived-flavor sub-chooser). `onCancel` renders an inline
 * cancel/back button; omit it when the host (e.g. a modal footer) owns navigation.
 */
export default function ColumnTypeChooser(props: {
    onPick: (sourceType: ColumnSourceType) => void;
    onCancel?: () => void;
    types?: ColumnTypeOption[];
    heading?: string;
    cancelLabel?: string;
}) {
    const { onPick, onCancel, types = CREATE_COLUMN_TYPES, heading = 'Choose the column type', cancelLabel = 'Back to columns' } = props;
    return (
        <SpaceBetween size="l">
            <Box variant="h4">{heading}</Box>
            {/* One row: a column per card (2 for the create wizard / derived flavors, 3 for the add
                chooser), so cards sit side by side rather than wrapping to a second row. */}
            <ColumnLayout columns={Math.min(types.length, 4) as 1 | 2 | 3 | 4}>
                {types.map((t) => (
                    <Container key={t.key ?? t.sourceType}>
                        {/* Fixed-height flex column keeps the cards the same size and pins Select to the
                            bottom, so a longer description on one card cannot misalign the buttons. */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                gap: '8px',
                                minHeight: '190px',
                            }}
                        >
                            {t.icon
                                ? <img src={CONSTANTS.IMAGE_PATH + t.icon} width={44} height={44} alt="" />
                                : <div style={{ height: '44px' }} />}
                            <Box variant="strong">{t.title}</Box>
                            <Box variant="small" color="text-body-secondary">{t.description}</Box>
                            <div style={{ marginTop: 'auto' }}>
                                <Button onClick={() => (t.onSelect ? t.onSelect() : onPick(t.sourceType as ColumnSourceType))}>Select</Button>
                            </div>
                        </div>
                    </Container>
                ))}
            </ColumnLayout>
            {onCancel && (
                <Box float="right">
                    <Button onClick={onCancel}>{cancelLabel}</Button>
                </Box>
            )}
        </SpaceBetween>
    );
}
